import {
  DealLensStatus,
  mergeDealLensSwipeStatus,
  normalizeDealLensStatus,
} from "./dealsConstants";
import { normalizeDealRow } from "./dealsApi";
import { resolveOfferAmount } from "@/utils/maoCalculator/offerAmountUtils";

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeThreadEmail(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isEmailLikeIdentifier(value) {
  return normalizeThreadEmail(value).includes("@");
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeDeallensToken(value) {
  return String(value ?? "")
    .trim()
    .replace(/^deallens#/i, "");
}

/**
 * Resolves collection_id from a deal or, when missing, from the sibling row that belongs
 * to the SAME offer lineage.
 *
 * Every offer in a thread shares the same `thread` but carries its own distinct
 * `collection_id`, so matching on `thread` alone would pull a different offer's
 * collection_id. The fallback therefore only borrows from a sibling whose deallens_id
 * matches this deal's own id or its `previous_deallens_id` predecessor.
 *
 * @param {object | null | undefined} deal
 * @param {object[]} [siblingDeals]
 * @returns {string}
 */
function unwrapDealLensRow(deal) {
  if (!deal || typeof deal !== "object") return deal;
  return deal._raw && typeof deal._raw === "object" ? deal._raw : deal;
}

export function resolveDealCollectionId(deal, siblingDeals = []) {
  const row = unwrapDealLensRow(deal);
  const direct =
    typeof row?.collection_id === "string" && row.collection_id.trim()
      ? row.collection_id.trim()
      : typeof row?.raw?.collection_id === "string" && row.raw.collection_id.trim()
        ? row.raw.collection_id.trim()
        : "";
  if (direct) return direct;

  if (!Array.isArray(siblingDeals) || siblingDeals.length === 0) return "";

  const dealToken = normalizeDeallensToken(row?.deallens_id ?? row?.id ?? row?.raw?.deallens_id);
  const previousToken = normalizeDeallensToken(
    row?.metadata?.previous_deallens_id ?? row?.raw?.metadata?.previous_deallens_id
  );
  if (!dealToken && !previousToken) return "";

  const donor = siblingDeals.find((sibling) => {
    const siblingRow = unwrapDealLensRow(sibling);
    if (!siblingRow || typeof siblingRow !== "object") return false;
    const collectionId =
      typeof siblingRow.collection_id === "string" ? siblingRow.collection_id.trim() : "";
    if (!collectionId) return false;
    const rowToken = normalizeDeallensToken(
      siblingRow.deallens_id ?? siblingRow.id ?? siblingRow.raw?.deallens_id
    );
    const rowPrevToken = normalizeDeallensToken(
      siblingRow?.metadata?.previous_deallens_id ?? siblingRow?.raw?.metadata?.previous_deallens_id
    );
    return Boolean(
      rowToken &&
        (rowToken === dealToken ||
          rowToken === previousToken ||
          (dealToken && rowPrevToken === dealToken))
    );
  });
  return donor ? String(donor.collection_id).trim() : "";
}

/**
 * Generates a new DealLens collection id for a newly started offer conversation.
 *
 * @returns {string}
 */
export function createDealCollectionId() {
  if (typeof globalThis?.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `collection-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

/**
 * Resolves collection_id for an outbound share/append. Reuses an explicit override,
 * the deal or sibling lineage, or creates a new id so optimistic list rows and
 * shareDealLens share the same collection scope on first contact.
 *
 * @param {object | null | undefined} deal
 * @param {object[]} [threadDeals]
 * @param {string | null | undefined} [explicitCollectionId]
 * @returns {string}
 */
export function resolveOutboundDealCollectionId(
  deal,
  threadDeals = [],
  explicitCollectionId = null
) {
  const direct = String(explicitCollectionId ?? "").trim();
  if (direct) return direct;
  const resolved = resolveDealCollectionId(deal, threadDeals);
  if (resolved) return resolved;
  return createDealCollectionId();
}

/**
 * Keeps only DealLens rows that belong to the selected offer's collection subthread.
 * Property threads share one `thread` id; each conversation row uses a distinct `collection_id`.
 *
 * @param {object[]} deals
 * @param {object | null | undefined} selectedDeal
 * @param {object[]} [siblingDeals]
 * @returns {object[]}
 */
export function filterThreadDealsByCollection(deals, selectedDeal, siblingDeals = []) {
  if (!Array.isArray(deals) || deals.length === 0) return [];

  const siblingPool = [...(Array.isArray(siblingDeals) ? siblingDeals : []), ...deals].filter(
    (row) => row && typeof row === "object"
  );
  const selectedCollectionId = resolveDealCollectionId(selectedDeal, siblingPool);
  if (!selectedCollectionId) return deals;

  return deals.filter((deal) => {
    if (!deal || typeof deal !== "object") return false;
    return resolveDealCollectionId(deal, siblingPool) === selectedCollectionId;
  });
}

/**
 * Resolves sender/receiver for a DealLens mutation based on the current user's role.
 *
 * @param {object} deal
 * @param {string} currentUserRole
 * @returns {{ sender: string, senderRole: string, receiver: string, receiverRole: string }}
 */
export function resolveDealLensParties(deal, currentUserRole) {
  const role = String(currentUserRole).toLowerCase();
  const dealSenderRole = String(deal?.sender_role ?? "").toLowerCase();

  if (role === dealSenderRole) {
    return {
      sender: deal.sender ?? "",
      senderRole: dealSenderRole,
      receiver: deal.receiver ?? "",
      receiverRole: String(deal.receiver_role ?? ""),
    };
  }
  return {
    sender: deal.receiver ?? "",
    senderRole: String(deal.receiver_role ?? "").toLowerCase(),
    receiver: deal.sender ?? "",
    receiverRole: String(deal.sender_role ?? ""),
  };
}

/**
 * Cognito/Dynamo principal for putDeallens / shareDealLens sender auth
 * (web-save-deallens requires sender to match the authenticated user).
 *
 * @param {object | null | undefined} authStore
 * @returns {string}
 */
export function resolveAuthenticatedDealLensSender(authStore) {
  const userId = String(authStore?.userId ?? "").trim();
  if (userId) return userId;
  const email = normalizeThreadEmail(authStore?.email);
  if (email) return email;
  return "";
}

/**
 * Party fields for DealLens writes: receiver/roles from deal context, sender from auth.
 *
 * @param {object | null | undefined} deal
 * @param {object | null | undefined} authStore
 * @returns {{ sender: string, senderRole: string, receiver: string, receiverRole: string }}
 */
export function resolvePutDeallensParties(deal, authStore) {
  const currentRole = String(authStore?.currentRole?.() ?? "").toLowerCase();
  const parties = resolveDealLensParties(deal, currentRole);
  const authenticatedSender = resolveAuthenticatedDealLensSender(authStore);
  if (!authenticatedSender) return parties;
  return { ...parties, sender: authenticatedSender };
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean }} context
 * @returns {boolean}
 */
export function isViewerDealReceiver(deal, { viewerEmail, isInvestor, isDistributeRole = false }) {
  if (!deal || !isInvestor || isDistributeRole) return false;

  const senderRole = String(deal?.sender_role ?? "")
    .trim()
    .toLowerCase();
  const receiverRole = String(deal?.receiver_role ?? "")
    .trim()
    .toLowerCase();
  const senderEmail = normalizeThreadEmail(deal?.sender);
  const receiverEmail = normalizeThreadEmail(deal?.receiver);
  const senderIsEmailLike = isEmailLikeIdentifier(deal?.sender);
  const receiverIsEmailLike = isEmailLikeIdentifier(deal?.receiver);
  const viewerIsSenderByEmail = Boolean(
    viewerEmail && senderIsEmailLike && viewerEmail === senderEmail
  );
  const viewerIsReceiverByEmail = Boolean(
    viewerEmail && receiverIsEmailLike && viewerEmail === receiverEmail
  );

  if (viewerIsReceiverByEmail) return true;
  if (viewerIsSenderByEmail) return false;
  if (!receiverIsEmailLike && receiverRole === "investor" && senderRole !== "investor") {
    return true;
  }
  return false;
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean }} context
 * @returns {boolean}
 */
export function isViewerDealSender(deal, { viewerEmail, isInvestor, isDistributeRole = false }) {
  if (!deal || !isInvestor || isDistributeRole) return false;

  const senderRole = String(deal?.sender_role ?? "")
    .trim()
    .toLowerCase();
  const receiverRole = String(deal?.receiver_role ?? "")
    .trim()
    .toLowerCase();
  const senderEmail = normalizeThreadEmail(deal?.sender);
  const receiverEmail = normalizeThreadEmail(deal?.receiver);
  const senderIsEmailLike = isEmailLikeIdentifier(deal?.sender);
  const receiverIsEmailLike = isEmailLikeIdentifier(deal?.receiver);
  const viewerIsSenderByEmail = Boolean(
    viewerEmail && senderIsEmailLike && viewerEmail === senderEmail
  );
  const viewerIsReceiverByEmail = Boolean(
    viewerEmail && receiverIsEmailLike && viewerEmail === receiverEmail
  );

  if (viewerIsSenderByEmail) return true;
  if (viewerIsReceiverByEmail) return false;
  if (!senderIsEmailLike && senderRole === "investor" && receiverRole !== "investor") {
    return true;
  }
  return false;
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean }} context
 * @returns {boolean}
 */
export function hasReceivedCounterOffer(deal, context) {
  if (normalizeDealLensStatus(deal?.status) !== DealLensStatus.COUNTERED) return false;
  return isViewerDealReceiver(deal, context);
}

const TERMINAL_OFFER_ACTION_STATUSES = new Set([
  DealLensStatus.ACCEPTED,
  DealLensStatus.REJECTED,
  DealLensStatus.EXPIRED,
  DealLensStatus.CANCELLED,
  DealLensStatus.WITHDRAWN,
]);

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isTerminalOfferActionStatus(status) {
  return TERMINAL_OFFER_ACTION_STATUSES.has(normalizeDealLensStatus(status));
}

/**
 * @param {object | null | undefined} deal
 * @returns {{ senderRole: string, receiverRole: string }}
 */
function resolveDealPartyRoles(deal) {
  const senderRole = String(deal?.sender_role ?? deal?.role ?? "")
    .trim()
    .toLowerCase();
  let receiverRole = String(deal?.receiver_role ?? "")
    .trim()
    .toLowerCase();
  if (!receiverRole && (senderRole === "investor" || senderRole === "realtor")) {
    receiverRole = senderRole === "investor" ? "realtor" : "investor";
  }
  return { senderRole, receiverRole };
}

/**
 * @param {object | null | undefined} deal
 * @param {string | null | undefined} currentRole
 * @returns {boolean}
 */
export function isViewerDealReceiverByRole(deal, currentRole) {
  const role = String(currentRole ?? "")
    .trim()
    .toLowerCase();
  const { receiverRole } = resolveDealPartyRoles(deal);
  return role !== "" && role === receiverRole;
}

/**
 * @param {object | null | undefined} deal
 * @param {string | null | undefined} currentRole
 * @returns {boolean}
 */
export function isViewerDealSenderByRole(deal, currentRole) {
  const role = String(currentRole ?? "")
    .trim()
    .toLowerCase();
  const { senderRole } = resolveDealPartyRoles(deal);
  return role !== "" && role === senderRole;
}

/**
 * @param {object | null | undefined} deal
 * @param {{ isDistributeRole?: boolean, currentRole?: string | null }} context
 * @returns {boolean}
 */
export function hasDistributorReceivedOffer(deal, { isDistributeRole = false, currentRole } = {}) {
  if (!deal || !isDistributeRole) return false;

  const status = normalizeDealLensStatus(deal?.status);
  if (status !== DealLensStatus.COUNTERED && status !== DealLensStatus.OPEN) return false;
  if (!isViewerDealReceiverByRole(deal, currentRole)) return false;

  const offerAmount = resolveOfferAmount(deal?.calculation);
  return Number.isFinite(Number(offerAmount)) && Number(offerAmount) > 0;
}

/**
 * @param {object | null | undefined} deal
 * @param {{ isDistributeRole?: boolean, currentRole?: string | null }} context
 * @returns {boolean}
 */
export function hasDistributorSentCounterAwaitingResponse(
  deal,
  { isDistributeRole = false, currentRole } = {}
) {
  if (!deal || !isDistributeRole) return false;
  if (normalizeDealLensStatus(deal?.status) !== DealLensStatus.COUNTERED) return false;
  if (!isViewerDealSenderByRole(deal, currentRole)) return false;

  const offerAmount = resolveOfferAmount(deal?.calculation);
  return Number.isFinite(Number(offerAmount)) && Number(offerAmount) > 0;
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null, propertyIsActive?: boolean, activeDeal?: object | null }} [context]
 * @returns {boolean}
 */
export function canEditSentOffer(deal, context = {}) {
  if (!deal || context.propertyIsActive === false) return false;

  const status = normalizeDealLensStatus(deal.status);
  if (status !== DealLensStatus.OPEN && status !== DealLensStatus.COUNTERED) return false;

  const dealId = normalizeDeallensToken(deal.deallens_id ?? deal.id);
  if (!dealId || /^optimistic:/i.test(dealId)) return false;

  if (context.activeDeal) {
    const activeId = normalizeDeallensToken(
      context.activeDeal.deallens_id ?? context.activeDeal.id
    );
    if (!activeId || activeId !== dealId) return false;
  }

  const offerAmount = resolveOfferAmount(deal.calculation);
  if (!Number.isFinite(Number(offerAmount)) || Number(offerAmount) <= 0) return false;

  if (context.isDistributeRole) {
    return isViewerDealSenderByRole(deal, context.currentRole);
  }
  return isViewerDealSender(deal, context);
}

/** Shown when a submit-time edit authorization re-check fails. */
export const OFFER_EDIT_UNAUTHORIZED_MESSAGE =
  "This offer can no longer be edited because its status changed.";

/**
 * Defense-in-depth re-check before persisting an offer edit.
 *
 * @param {object | null | undefined} deal
 * @param {Parameters<typeof canEditSentOffer>[1]} [context]
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function assertCanEditSentOffer(deal, context = {}) {
  if (canEditSentOffer(deal, context)) {
    return { ok: true };
  }
  return { ok: false, message: OFFER_EDIT_UNAUTHORIZED_MESSAGE };
}

/**
 * Whether the current viewer may take the next offer action on this thread.
 *
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [context]
 * @returns {boolean}
 */
export function canViewerRespondToOfferThread(deal, context = {}) {
  if (!deal) return false;

  const status = normalizeDealLensStatus(deal.status);
  if (isTerminalOfferActionStatus(status)) return false;

  const { isInvestor, isDistributeRole = false, currentRole } = context;

  if (status === DealLensStatus.COUNTERED) {
    if (isInvestor && !isDistributeRole) {
      return hasReceivedCounterOffer(deal, context);
    }
    if (isDistributeRole) {
      return isViewerDealReceiverByRole(deal, currentRole);
    }
    return false;
  }

  if (status === DealLensStatus.OPEN) {
    return !hasSentOfferAwaitingResponse(deal, context);
  }

  if (status === DealLensStatus.MESSAGE) {
    return true;
  }

  return false;
}

/**
 * Merges an optimistic offer-action patch into a deal snapshot.
 *
 * @param {object | null | undefined} deal
 * @param {object | null | undefined} patch
 * @returns {object | null | undefined}
 */
export function mergeDealPatch(deal, patch) {
  if (!deal) return deal;
  if (!patch || typeof patch !== "object") return deal;

  let mergedMetadata = deal.metadata;
  if (patch.metadata) {
    mergedMetadata = { ...(deal.metadata || {}), ...patch.metadata };
    if (
      Object.prototype.hasOwnProperty.call(patch.metadata, "message") &&
      patch.metadata.message == null
    ) {
      delete mergedMetadata.message;
    }
  }

  return {
    ...deal,
    ...patch,
    calculation: patch.calculation
      ? { ...(deal.calculation || {}), ...patch.calculation }
      : deal.calculation,
    metadata: mergedMetadata,
  };
}

/**
 * Merges collection-tail display fields onto a hub list row without replacing hub identity.
 *
 * @param {object | null | undefined} hubRow
 * @param {object | null | undefined} tailRow
 * @returns {object}
 */
export function buildHubDisplayRow(hubRow, tailRow) {
  const base = hubRow && typeof hubRow === "object" ? hubRow : tailRow;
  if (!tailRow || typeof tailRow !== "object") return base;

  let mergedMetadata = base.metadata;
  if (tailRow.metadata && typeof tailRow.metadata === "object") {
    mergedMetadata = { ...(base.metadata || {}), ...tailRow.metadata };
  }

  const tailCollectionId = String(tailRow.collection_id ?? "").trim();

  return {
    ...base,
    status: tailRow.status ?? base.status,
    sender: tailRow.sender ?? base.sender,
    receiver: tailRow.receiver ?? base.receiver,
    sender_role: tailRow.sender_role ?? base.sender_role,
    receiver_role: tailRow.receiver_role ?? base.receiver_role,
    calculation: tailRow.calculation
      ? { ...(base.calculation || {}), ...tailRow.calculation }
      : base.calculation,
    metadata: mergedMetadata,
    insert_date: tailRow.insert_date ?? base.insert_date,
    last_modified: tailRow.last_modified ?? base.last_modified,
    // Keep hub list identity, but carry the active collection tip so DealLens
    // detail merge/selection use the same list id as PropertyOfferCache.
    ...(tailCollectionId ? { collection_id: tailCollectionId } : {}),
    id: base.id,
    deallens_id: base.deallens_id,
    swipe_status: base.swipe_status,
  };
}

/**
 * Replaces a deal snapshot with a server/normalized row instead of merging into prior state.
 * Inherits swipe_status, collection_id, and property_id from priorRow when the incoming row omits them.
 *
 * @param {object} serverRow
 * @param {{ priorRow?: object|null, fetchParams?: { sender?: string, user_id?: string, customer?: string }|null, listCustomer?: string|null }} [options]
 * @returns {object}
 */
export function substituteDealRow(
  serverRow,
  { priorRow = null, fetchParams = null, listCustomer = null } = {}
) {
  if (!serverRow || typeof serverRow !== "object") {
    throw new Error("substituteDealRow: serverRow is required");
  }

  const normalized =
    serverRow.raw != null && (serverRow.deallens_id != null || serverRow.id != null)
      ? serverRow
      : normalizeDealRow(serverRow, 0);

  let next = normalized;
  if (fetchParams && typeof fetchParams === "object") {
    next = {
      ...next,
      sender: next.sender || fetchParams.sender || "",
      receiver: next.receiver || fetchParams.user_id || "",
      customer: next.customer || fetchParams.customer || listCustomer || "",
    };
  } else if (listCustomer && !String(next.customer ?? "").trim()) {
    next = { ...next, customer: listCustomer };
  }

  return mergeDealLensSwipeStatus(next, priorRow);
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean }} context
 * @returns {boolean}
 */
export function hasSentOfferAwaitingResponse(deal, context) {
  const status = normalizeDealLensStatus(deal?.status);
  if (status !== DealLensStatus.COUNTERED && status !== DealLensStatus.OPEN) return false;

  const isSender = context.isDistributeRole
    ? isViewerDealSenderByRole(deal, context.currentRole)
    : isViewerDealSender(deal, context);
  if (!isSender) return false;

  const offerAmount = resolveOfferAmount(deal?.calculation);
  return Number.isFinite(Number(offerAmount)) && Number(offerAmount) > 0;
}

/**
 * Controls when opportunities should show compact offer terms.
 *
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [context]
 * @returns {boolean}
 */
export function shouldShowOfferTermsSummary(deal, context = {}) {
  if (!deal) return false;
  if (normalizeDealLensStatus(deal.status) === DealLensStatus.ACCEPTED) return true;
  if (hasReceivedCounterOffer(deal, context)) return true;
  return hasSentOfferAwaitingResponse(deal, context);
}

/** @typedef {{ label: string, variant: 'success'|'warning'|'info'|'error'|'muted', icon?: 'mail' }} OpportunityOfferStatusPill */

const STATUS_PILL_BY_DEAL_STATUS = Object.freeze({
  [DealLensStatus.ACCEPTED]: { label: "Accepted", variant: "success" },
  [DealLensStatus.REJECTED]: { label: "Rejected", variant: "error" },
  [DealLensStatus.EXPIRED]: { label: "Expired", variant: "muted" },
  [DealLensStatus.CANCELLED]: { label: "Cancelled", variant: "muted" },
  [DealLensStatus.WITHDRAWN]: { label: "Withdrawn", variant: "muted" },
  [DealLensStatus.DRAFT]: { label: "Draft", variant: "muted" },
});

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} context
 * @returns {{ isReceiver: boolean, viewerKnown: boolean }}
 */
export function resolveMessageThreadViewerState(deal, context = {}) {
  const { isInvestor, isDistributeRole = false, currentRole } = context;
  let viewerKnown = false;
  let isReceiver = false;

  if (isDistributeRole && currentRole) {
    viewerKnown = true;
    isReceiver = isViewerDealReceiverByRole(deal, currentRole);
  } else if (isInvestor) {
    viewerKnown = true;
    isReceiver = isViewerDealReceiver(deal, context);
  }

  return { isReceiver, viewerKnown };
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [context]
 * @returns {{ label: string, datePrefix: string }}
 */
export function resolveMessageThreadDisplayCopy(deal, context = {}) {
  const { isReceiver, viewerKnown } = resolveMessageThreadViewerState(deal, context);

  if (!viewerKnown) {
    return { label: "Message", datePrefix: "Received on" };
  }

  return {
    label: isReceiver ? "New message" : "Message sent",
    datePrefix: isReceiver ? "Received on" : "Sent on",
  };
}

/**
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} context
 * @returns {OpportunityOfferStatusPill}
 */
function resolveMessageOfferStatusPill(deal, context) {
  const { label } = resolveMessageThreadDisplayCopy(deal, context);

  return { label, variant: "info", icon: "mail" };
}

/**
 * Derives the opportunities list offer-status pill for a deal thread.
 *
 * @param {object | null | undefined} deal
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [context]
 * @returns {OpportunityOfferStatusPill | null}
 */
export function getOpportunityOfferStatusPill(deal, context = {}) {
  if (!deal) return null;

  const status = normalizeDealLensStatus(deal.status);
  const terminalPill = STATUS_PILL_BY_DEAL_STATUS[status];
  if (terminalPill) return terminalPill;

  if (status === DealLensStatus.MESSAGE) {
    return resolveMessageOfferStatusPill(deal, context);
  }

  if (String(deal?.metadata?.offer_event ?? "").toUpperCase() === "UPDATED") {
    return { label: "Offer updated", variant: "info" };
  }

  if (hasReceivedCounterOffer(deal, context)) {
    return { label: "Countered", variant: "warning" };
  }

  if (hasSentOfferAwaitingResponse(deal, context)) {
    return { label: "Offer sent", variant: "info" };
  }

  return null;
}

/**
 * Builds a partial deal patch for optimistic opportunities list updates after offer actions.
 *
 * @param {object} deal
 * @param {object} options
 * @param {string} options.currentRole
 * @param {string} options.status
 * @param {string} [options.message]
 * @param {number|null} [options.offerAmount]
 * @param {number|null} [options.investorOfferAmount]
 * @param {object|null} [options.offerTerms]
 * @param {object|null} [options.calculation]
 * @returns {object}
 */
export function buildOpportunitiesListDealPatch(
  deal,
  {
    currentRole,
    status,
    message,
    offerAmount,
    investorOfferAmount,
    offerTerms,
    calculation,
    collectionId,
    metadata,
  }
) {
  const parties = resolveDealLensParties(deal, currentRole);
  const hasMessageArg = message !== undefined;
  const sanitizedMessage = typeof message === "string" ? message.trim() : "";
  /** @type {Record<string, unknown>} */
  const patch = {
    status,
    sender: parties.sender,
    receiver: parties.receiver,
    sender_role: parties.senderRole,
    receiver_role: parties.receiverRole,
  };
  const normalizedCollectionId = String(collectionId ?? "").trim();
  if (normalizedCollectionId) {
    patch.collection_id = normalizedCollectionId;
  }

  if (offerAmount != null) {
    patch.calculation = { offer_amount: offerAmount };
  }
  if (calculation && typeof calculation === "object") {
    patch.calculation = { ...(patch.calculation || {}), ...calculation };
  }
  const metadataPatch = metadata && typeof metadata === "object" ? { ...metadata } : {};
  // Do not inherit tip chat text onto a new append unless an explicit non-empty note was provided.
  // Otherwise a MESSAGE tip's body sticks to the optimistic COUNTERED row and duplicates in the
  // thread until refresh (combined bubble + bare server counter).
  const isOfferAppend =
    normalizeDealLensStatus(status) === DealLensStatus.COUNTERED ||
    normalizeDealLensStatus(status) === DealLensStatus.OPEN;
  const clearInheritedMessage =
    (!hasMessageArg && isTerminalOfferActionStatus(status)) ||
    (isOfferAppend && (!hasMessageArg || !sanitizedMessage));
  if (hasMessageArg || clearInheritedMessage) {
    metadataPatch.message = hasMessageArg && sanitizedMessage ? sanitizedMessage : null;
  }
  if (investorOfferAmount != null) {
    metadataPatch.investor_offer_amount = investorOfferAmount;
  }
  if (offerTerms && typeof offerTerms === "object") {
    metadataPatch.offer_terms = offerTerms;
  }
  if (Object.keys(metadataPatch).length > 0) {
    patch.metadata = metadataPatch;
  }

  return patch;
}
